import * as XLSX from 'xlsx';
import { getBulkSheetParamColumns } from './bulkSheetValidation';

function buildErrorSummary(errors = []) {
  return errors.map((error) => error.message).join('; ');
}

function buildErrorFields(errors = []) {
  return errors.map((error) => error.field).join(',');
}

export function buildLeadTemplateErrorSheetWorkbook({
  template,
  rowResults = [],
  includeAllRows = true,
  guideLines = [],
}) {
  const paramColumns = getBulkSheetParamColumns(template);
  const dataColumns = ['phone_number', ...paramColumns.filter((col) => col !== 'phone_number')];

  const sourceRows = includeAllRows
    ? rowResults
    : rowResults.filter((entry) => entry.errors?.length);

  const sheetRows = sourceRows.map((entry) => {
    const output = {
      row_number: entry.rowNumber,
    };

    dataColumns.forEach((col) => {
      output[col] = entry.row?.[col] ?? '';
    });

    output.error_summary = buildErrorSummary(entry.errors ?? []);
    output.error_fields = buildErrorFields(entry.errors ?? []);
    return output;
  });

  const headers = ['row_number', ...dataColumns, 'error_summary', 'error_fields'];
  const dataSheet = XLSX.utils.json_to_sheet(sheetRows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, dataSheet, 'data_with_errors');

  if (guideLines.length) {
    const guideRows = guideLines.map((line) => ({ instruction: line }));
    const guideSheet = XLSX.utils.json_to_sheet(guideRows, { header: ['instruction'] });
    XLSX.utils.book_append_sheet(workbook, guideSheet, 'error_guide');
  }

  return workbook;
}

export function downloadLeadTemplateErrorSheet({
  template,
  rowResults = [],
  guideLines = [],
}) {
  const workbook = buildLeadTemplateErrorSheetWorkbook({
    template,
    rowResults,
    includeAllRows: true,
    guideLines,
  });
  const safeName = String(template?.name ?? 'template').replace(/[^\w.-]+/g, '_');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  XLSX.writeFile(workbook, `template-send-errors-${safeName}-${stamp}.xlsx`);
}

function sheetHeaderMap(sheet) {
  const range = XLSX.utils.decode_range(sheet['!ref']);
  const headers = new Map();
  for (let column = range.s.c; column <= range.e.c; column += 1) {
    const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })];
    const name = cell?.v == null ? '' : String(cell.v).trim();
    if (name) headers.set(name, column);
  }
  return { range, headers };
}

function dataRowIndexes(sheet, range) {
  const indexes = [];
  for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
    let hasValue = false;
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
      if (cell?.v != null && cell.v !== '') {
        hasValue = true;
        break;
      }
    }
    if (hasValue) indexes.push(row);
  }
  return indexes;
}

function writeTextCell(sheet, row, column, value) {
  const address = XLSX.utils.encode_cell({ r: row, c: column });
  if (!value) {
    delete sheet[address];
    return;
  }
  sheet[address] = { t: 's', v: value };
}

export function appendBulkErrorColumns(workbook, rowResults = []) {
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const { range, headers } = sheetHeaderMap(sheet);
  let summaryColumn = headers.get('error_summary');
  let fieldsColumn = headers.get('error_fields');
  let nextColumn = range.e.c + 1;
  if (summaryColumn == null) {
    summaryColumn = nextColumn;
    nextColumn += 1;
  }
  if (fieldsColumn == null) {
    fieldsColumn = nextColumn;
  }

  writeTextCell(sheet, range.s.r, summaryColumn, 'error_summary');
  writeTextCell(sheet, range.s.r, fieldsColumn, 'error_fields');

  const rowIndexes = dataRowIndexes(sheet, range);
  rowIndexes.forEach((rowIndex, index) => {
    const errors = rowResults[index]?.errors ?? [];
    writeTextCell(sheet, rowIndex, summaryColumn, buildErrorSummary(errors));
    writeTextCell(sheet, rowIndex, fieldsColumn, buildErrorFields(errors));
  });

  range.e.c = Math.max(range.e.c, summaryColumn, fieldsColumn);
  sheet['!ref'] = XLSX.utils.encode_range(range);
  return workbook;
}

export async function buildBulkErrorWorkbook(file, rowResults = []) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  return appendBulkErrorColumns(workbook, rowResults);
}

export function errorSheetFileName(fileName = 'bulk-send.xlsx') {
  const base = String(fileName).replace(/\.(xlsx|xls)$/i, '');
  return `${base}-errors.xlsx`;
}

export async function downloadBulkErrorSheet({ file, rowResults = [] }) {
  const workbook = await buildBulkErrorWorkbook(file, rowResults);
  XLSX.writeFile(workbook, errorSheetFileName(file?.name));
}
